import React, { useEffect, useState } from "react";
import {
  Building2,
  MapPin,
  Phone,
  User,
  Plus,
  Search,
  Building,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

const ProjectList = () => {
  const [projects, setProjects] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrgId, setSelectedOrgId] = useState("");
  const BASE_URL = process.env.REACT_APP_BASE_URL;
  const navigate = useNavigate();

  const filteredProjects = projects.filter((project) =>
    (project.project_name || "")
      .toLowerCase()
      .includes(searchTerm.toLowerCase())
  );

  const fetchProjects = async (orgId) => {
    try {
      const response = await fetch(`${BASE_URL}/orgdetails/projects`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ org_id: orgId }),
      });
      if (!response.ok) throw new Error("Failed to fetch projects");
      const data = await response.json();
      setProjects(data);
    } catch (err) {
      setProjects([]);
      console.error(err);
    }
  };

  const fetchOrganizations = async () => {
    try {
      const response = await fetch(`${BASE_URL}/orgdetails/getorganizations`);
      if (!response.ok) throw new Error("Failed to fetch organizations");
      const data = await response.json();
      setOrganizations(data);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchOrganizations();
  }, []);

  useEffect(() => {
    if (selectedOrgId) {
      fetchProjects(selectedOrgId);
    }
  }, [selectedOrgId]);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="space-y-8">
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-6">
                <div className="p-4 bg-blue-50 rounded-xl">
                  <Building2 className="h-8 w-8 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">
                    Total Projects
                  </p>
                  <h2 className="text-3xl font-bold text-gray-900">
                    {filteredProjects.length}
                  </h2>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <Building className="h-5 w-5 text-gray-400" />
                <select
                  value={selectedOrgId}
                  onChange={(e) => setSelectedOrgId(e.target.value)}
                  className="block w-64 pl-3 pr-10 py-2 text-base border border-gray-200 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition duration-150"
                >
                  <option key={0} value={0} >Select the organization</option>
                  {organizations.map((org) => (
                    <option key={org.id} value={org.id}>
                      {org.org_name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                placeholder="Search projects..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="block w-full pl-10 pr-3 py-3 text-sm border border-gray-200 rounded-xl bg-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition duration-150"
              />
            </div>
            <button
              onClick={() => navigate("/projects/new")}
              className="inline-flex items-center px-6 py-3 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 transition duration-150 shadow-sm hover:shadow active:transform active:scale-[0.98]"
            >
              <Plus className="h-5 w-5 mr-2" />
              Add Project
            </button>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="p-6 border-b border-gray-100">
              <h2 className="text-xl font-semibold text-gray-900">
                Projects Directory
              </h2>
              <p className="mt-1 text-sm text-gray-500">
                view all your project details
              </p>
            </div>

            <div className="divide-y divide-gray-100">
              {filteredProjects.length === 0 ? (
                <div className="p-8 text-center">
                  <Building2 className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-4 text-lg font-medium text-gray-900">
                    No projects found
                  </h3>
                  <p className="mt-2 text-sm text-gray-500">
                    project name not Found.Add project.
                  </p>
                  <button
                    onClick={() => navigate("/projects/new")}
                    className="mt-4 inline-flex items-center px-4 py-2 rounded-lg text-sm font-medium bg-blue-50 text-blue-600 hover:bg-blue-100 transition duration-150"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add Project
                  </button>
                </div>
              ) : (
                filteredProjects.map((project) => (
                  <div
                    key={project.id}
                    className="p-6 hover:bg-gray-50 transition-colors group"
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3">
                          <div className="p-2 bg-blue-50 rounded-lg group-hover:bg-blue-100 transition-colors">
                            <Building2 className="h-6 w-6 text-blue-600" />
                          </div>
                          <div>
                            <h3 className="text-lg font-semibold text-gray-900 text-left">
                              {project.project_name}
                            </h3>
                            <div className="mt-2 space-y-2">
                              <p className="flex items-center text-sm text-gray-600">
                                <MapPin className="h-4 w-4 mr-2 text-gray-400" />
                                {project.address}
                              </p>
                              <p className="flex items-center text-sm text-gray-600">
                                <Phone className="h-4 w-4 mr-2 text-gray-400" />
                                {project.phone_number}
                              </p>
                              <p className="flex items-center text-sm text-gray-600">
                                <User className="h-4 w-4 mr-2 text-gray-400" />
                                {project.contact_person}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectList;
